import { Topbar } from '@eduzz/ui-layout';
import { Cancel, CheckCircle, Warning } from '@material-ui/icons';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useObservable } from 'react-use-observable';
import chartColors from 'scenes/Producer/Dashboard/chartOptions';
import { useStyles } from 'v2/components/Pages/SubscriptionRecurringDate/ResolveChange/styles';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import { HttpStatus } from 'v2/interfaces/http';
import { ResolveSubscriptionChangeStatus } from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

import type { ResolveSubscriptionChangeResponse } from 'v2/interfaces/subscriptions';

type MessageType = {
  icon: React.ReactNode;
  description: string;
};

const iconSize = 72;
const iconStyle = { fontSize: iconSize };

export default function ResolveRecurringDateChange() {
  return (
    <div>
      <Topbar />
      <ResolveRecurringDateChangeResultSection />
    </div>
  );
}

function ResolveRecurringDateChangeResultSection() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const token = params.get('token');
  const action = params.get('action');

  const response = useObservable(() => {
    return subscriptionsService.resolveRecurringDateChange(token, action);
  }, []);

  const [body, error] = response;

  if (!body && !error) {
    return <GlobalLoading />;
  }

  if (error?.response?.status === HttpStatus.UnprocessableEntity) {
    return (
      <MessageContainer
        icon={<Warning htmlColor={chartColors.yellow.background} style={{ ...iconStyle }} />}
        description='Este link já foi utilizado portanto não é mais válido.'
      />
    );
  }

  if (error) {
    return (
      <MessageContainer
        icon={<Warning htmlColor={chartColors.yellow.background} style={{ ...iconStyle }} />}
        description='Ocorreu um erro ao processar a solicitação. Por favor, tente novamente mais tarde.'
      />
    );
  }

  const messageMapper: Record<ResolveSubscriptionChangeResponse['data']['status'], MessageType> = {
    [ResolveSubscriptionChangeStatus.EXPIRED]: {
      icon: <Warning htmlColor={chartColors.yellow.background} style={{ ...iconStyle }} />,
      description:
        'Este link expirou. O prazo de resposta de 48 horas foi ultrapassado. Para alterar a data de vencimento, entre em contato com o produtor.',
    },
    [ResolveSubscriptionChangeStatus.APPROVED]: {
      icon: <CheckCircle htmlColor={chartColors.green.background} style={{ ...iconStyle }} />,
      description: 'Solicitação confirmada! A nova data de vencimento será atualizada no seu contrato conforme solicitado.',
    },
    [ResolveSubscriptionChangeStatus.REJECTED]: {
      icon: <Cancel htmlColor={chartColors.red.background} style={{ ...iconStyle }} />,
      description: 'Alteração recusada. Sua data de vencimento continuará a mesma.',
    },
  };

  const message = messageMapper[body.data.status] || {
    icon: <Warning htmlColor={chartColors.yellow.background} style={{ ...iconStyle }} />,
    description: 'Algo deu errado. Por favor, tente novamente mais tarde.',
  };

  return <MessageContainer icon={message.icon} description={message.description} />;
}

function MessageContainer({ icon, description }: MessageType) {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <div className={styles.message}>
        <span>{icon}</span>
        <span>{description}</span>
      </div>
    </div>
  );
}
